import { type ReactNode, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Minimal accessible tooltip primitive.
//
// - Trigger wraps children in a focusable inline span (role=button).
// - Opens on hover, focus, click; closes on blur, mouse leave, Esc.
// - Renders into a portal so it escapes overflow:hidden containers
//   (the header tape clips otherwise).
// - 20-line CSS contract — no transition spam, no animation slop.
//
// Use:
//   <Tooltip content={<>…</>} ariaLabel="Show details">{children}</Tooltip>

interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  ariaLabel?: string;
  /** placement; defaults to bottom */
  placement?: 'top' | 'bottom';
  /** override popup max-width; defaults to 320 */
  maxWidth?: number;
}

export function Tooltip({
  content,
  children,
  ariaLabel,
  placement = 'bottom',
  maxWidth = 320,
}: TooltipProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);
  const id = useId();

  const reposition = () => {
    const trig = triggerRef.current;
    const pop = popRef.current;
    if (!trig || !pop) return;
    const tr = trig.getBoundingClientRect();
    const pr = pop.getBoundingClientRect();
    const margin = 6;
    let top = placement === 'top' ? tr.top - pr.height - margin : tr.bottom + margin;
    let left = tr.left + tr.width / 2 - pr.width / 2;
    // Clamp into viewport.
    left = Math.max(8, Math.min(left, window.innerWidth - pr.width - 8));
    top = Math.max(8, Math.min(top, window.innerHeight - pr.height - 8));
    setPos({ top, left });
  };

  useLayoutEffect(() => {
    if (!open) return;
    reposition();
    // Re-position on scroll/resize while open.
    const handler = () => reposition();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, placement]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      <span
        ref={triggerRef}
        tabIndex={0}
        role="button"
        aria-label={ariaLabel}
        aria-describedby={open ? id : undefined}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        style={{ display: 'inline-flex', cursor: 'help', outline: 'none' }}
      >
        {children}
      </span>
      {open &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={popRef}
            id={id}
            role="tooltip"
            style={{
              position: 'fixed',
              top: pos?.top ?? -9999,
              left: pos?.left ?? -9999,
              maxWidth,
              padding: '8px 10px',
              background: 'var(--bg-pane)',
              border: '1px solid var(--bd-strong)',
              color: 'var(--fg-1)',
              fontFamily: 'var(--sans)',
              fontSize: 11,
              lineHeight: 1.45,
              zIndex: 100,
              boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
              pointerEvents: 'none',
            }}
          >
            {content}
          </div>,
          document.body,
        )}
    </>
  );
}
